/**
 * Helpers for detecting "access denied" responses from the API.
 *
 * The backend returns HTTP 403 when the caller's subscription does not include
 * a dataset/feature (detail = "messages.insufficient_subscription_features").
 * Visualizations should render a dedicated restricted-access state for these,
 * rather than the generic "failed to load" error.
 */

/**
 * True when the given error represents an access-denied / insufficient-
 * subscription response (HTTP 403).
 *
 * Accepts a raw axios error, a normalized `{ status }` object, or a fetch-style
 * `{ response: { status } }` shape.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isAccessDeniedError(err) {
  if (!err) {
    return false;
  }

  const status =
    err?.response?.status ?? err?.status ?? err?.statusCode ?? err?.response?.data?.status ?? null;

  return Number(status) === 403;
}

/**
 * Pull the upgrade context out of a `403 entitlement_missing` axios error so a
 * locked visualization can render a "Requires <plan>" message and deep-link
 * the upgrade button straight to the right plan.
 *
 * The backend detail (see Client.js) carries `missing_keys` (the gated
 * entitlement keys) and an optional resolved `offer` (BillingOffer with
 * `name` / `offer_type` / `stripe_price_id` / `self_serve`). Returns safe
 * empty defaults for any other error shape.
 *
 * @param {unknown} err
 * @returns {{ entitlementKey: string, offer: object|null }}
 */
export function extractAccessDeniedDetail(err) {
  const detail = err?.response?.data?.detail;
  if (!detail || typeof detail !== 'object') {
    return { entitlementKey: '', offer: null };
  }
  const keys = Array.isArray(detail.missing_keys) ? detail.missing_keys : [];
  const firstKey = keys.find((key) => typeof key === 'string' && key.trim().length > 0);
  return {
    entitlementKey: firstKey ? String(firstKey).trim() : '',
    offer: detail.offer && typeof detail.offer === 'object' ? detail.offer : null,
  };
}

/**
 * The backend detail string used for subscription-feature gating. Exposed for
 * callers that want to distinguish a subscription gate from other 403s.
 */
export const INSUFFICIENT_SUBSCRIPTION_DETAIL = 'messages.insufficient_subscription_features';
